import { h, Host } from "@stencil/core";
import { ButtonLabelVisibility, ButtonSize, ButtonVariant } from "./button.enum";
/**
 * @slot (default) - Content is placed inside the button if provided without a slot name
 * @slot "icon" - Should be used to place a custom icon
 */
export class Button {
    constructor() {
        this.type = undefined;
        this.name = undefined;
        this.disabled = false;
        this.selected = false;
        this.size = ButtonSize.Default;
        this.label = undefined;
        this.multiline = false;
        this.icon = undefined;
        this.iconPlacement = "start";
        this.labelVisibility = ButtonLabelVisibility.Default;
        this.variant = ButtonVariant.Default;
        this.href = undefined;
        this.download = false;
        this.target = "_self";
    }
    disabledAttributeObserver;
    host;
    internals;
    onClick(e) {
        if (this.disabled) {
            e.stopPropagation();
        }
    }
    /** fixes a problem where the attribute disabled="false" is interpreted differently by the browser - preventing events */
    checkDisabled() {
        if (!this.disabled) {
            this.host.removeAttribute("disabled");
        }
    }
    connectedCallback() {
        this.disabledAttributeObserver = new MutationObserver(() => {
            if (this.host.hasAttribute("disabled")) {
                this.checkDisabled();
            }
        });
        this.disabledAttributeObserver.observe(this.host, { attributes: true, attributeFilter: ["disabled"] });
        this.checkDisabled();
    }
    disconnectedCallback() {
        this.disabledAttributeObserver.disconnect();
    }
    getTabIndex = () => {
        if (this.disabled) {
            return -1;
        }
        const tabindexAttr = this.host.getAttribute("tabindex");
        if (tabindexAttr !== null && !Number.isNaN(parseInt(tabindexAttr, 10))) {
            return parseInt(tabindexAttr, 10);
        }
        return 0;
    };
    dispatchFormSubmit() {
        if (this.type === "submit" && this.internals.form) {
            this.internals.form.requestSubmit();
        }
    }
    render() {
        const baseClass = "ux-button";
        const hasCustomIcon = this.host.querySelector("[slot=icon]") !== null;
        const hasIcon = Boolean(hasCustomIcon || (this.icon && this.icon.length > 0));
        const hideLabel = [ButtonSize.Icon, ButtonSize.IconStretched].includes(this.size);
        return (h(Host, { key: 'f9eed10f1a1035a5973347e87b6a42105826bb17', variant: this.variant, size: this.size, iconPlacement: this.iconPlacement, icon: this.icon }, !this.href && (h("button", { key: 'aceb4547ea407bc322e3dc87e75a5d307059f071', class: {
                [`${baseClass}__label-visibility ${baseClass}__label-visibility--${this.labelVisibility}`]: !hideLabel && this.labelVisibility !== ButtonLabelVisibility.Default
            }, tabIndex: this.getTabIndex(), type: this.type, name: this.name, disabled: this.disabled, "aria-label": this.label, onClick: () => this.dispatchFormSubmit() }, h("span", { key: '11a9df7b09584eb5058cc8d874d2e936f8bb24f1', class: {
                [`${baseClass}__icon`]: hasIcon,
                [`${baseClass}__icon--none`]: !hasIcon,
                [`${baseClass}__icon--custom`]: hasCustomIcon
            } }, h("slot", { key: '76a12a0c09173d829df53992657af7b12f974bed', name: "icon" }, this.icon && h("ux-icon", { key: '0fa9a9e84605407bd2fe98c6c360f97de3db469f', name: this.icon }))), h("span", { key: 'd8f2c32652ce35c58b2a0bb17a3d22b1dba7b1ea', class: {
                [`${baseClass}__label`]: !hideLabel,
                [`${baseClass}__label--truncate`]: !hideLabel && !this.multiline,
                [`${baseClass}__label--hidden`]: hideLabel
            } }, h("slot", { key: '3a09f02d074f060d264f22d1419fbb95bf783689' }, this.label)))), this.href && (h("a", { key: 'e3b246aeeb9736192d982f2f386f678dce373dea', part: "button", tabIndex: this.getTabIndex(), "aria-label": this.label, href: !this.disabled ? this.href : undefined, "aria-disabled": this.disabled ? "true" : false, target: this.target, ...(this.download && {
                download: ""
            }) }, h("span", { key: '35f67535a58c07b36334d54142c8ab2c83969d9a', "aria-hidden": "true", class: {
                [`${baseClass}__icon`]: hasIcon,
                [`${baseClass}__icon--none`]: !hasIcon,
                [`${baseClass}__icon--custom`]: hasCustomIcon
            } }, h("slot", { key: '3f84d0d71e4435fe48d4d4b35b20f0b595a2c8c3', name: "icon" }, this.icon && h("ux-icon", { key: '5442a6b0378354d50f7ac3b6f49879646ff7dfa2', name: this.icon }))), h("span", { key: '48c0678e7d088974fa07800133a688827199321f', class: {
                [`${baseClass}__label`]: true,
                [`${baseClass}__label--truncate`]: !this.multiline,
                [`${baseClass}__label--hidden`]: this.size === "icon"
            } }, h("slot", { key: 'a392322c52c410ad7fcef6aea391943cec7ca558' }, this.label))))));
    }
    static get is() { return "ux-button"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["button.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["button.css"]
        };
    }
    static get properties() {
        return {
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"button\" | \"submit\" | \"reset\"",
                    "resolved": "\"button\" | \"reset\" | \"submit\" | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The type attribute of the button element."
                },
                "attribute": "type",
                "reflect": false
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name attribute of the button element."
                },
                "attribute": "name",
                "reflect": true
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true`, the user cannot interact with the button."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "selected": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true`, the button shows a selected state."
                },
                "attribute": "selected",
                "reflect": true,
                "defaultValue": "false"
            },
            "size": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"default\" | \"icon\" | \"icon-stretched\" | ButtonSize",
                    "resolved": "\"default\" | \"icon\" | \"icon-stretched\" | ButtonSize.Default | ButtonSize.Icon | ButtonSize.IconStretched",
                    "references": {
                        "ButtonSize": {
                            "location": "import",
                            "path": "./button.enum",
                            "id": "src/components/button/button.enum.ts::ButtonSize"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The size of the button. Use `icon` for a button with just an icon."
                },
                "attribute": "size",
                "reflect": false,
                "defaultValue": "ButtonSize.Default"
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Label text of the button, required for better accessibility"
                },
                "attribute": "label",
                "reflect": false
            },
            "multiline": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true`, the label is not truncated."
                },
                "attribute": "multiline",
                "reflect": false,
                "defaultValue": "false"
            },
            "icon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name of the icon to show next to the label."
                },
                "attribute": "icon",
                "reflect": false
            },
            "iconPlacement": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"start\" | \"end\"",
                    "resolved": "\"end\" | \"start\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Horizontal placement of the icon."
                },
                "attribute": "icon-placement",
                "reflect": false,
                "defaultValue": "\"start\""
            },
            "labelVisibility": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"default\" | \"s\" | \"m\" | \"l\" | \"xl\" | ButtonLabelVisibility",
                    "resolved": "\"default\" | \"l\" | \"m\" | \"s\" | \"xl\" | ButtonLabelVisibility.Default | ButtonLabelVisibility.L | ButtonLabelVisibility.M | ButtonLabelVisibility.S | ButtonLabelVisibility.XL",
                    "references": {
                        "ButtonLabelVisibility": {
                            "location": "import",
                            "path": "./button.enum",
                            "id": "src/components/button/button.enum.ts::ButtonLabelVisibility"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Breakpoint when label should be visible"
                },
                "attribute": "label-visibility",
                "reflect": false,
                "defaultValue": "ButtonLabelVisibility.Default"
            },
            "variant": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"default\" | \"primary\" | \"no-border\" | ButtonVariant",
                    "resolved": "\"default\" | \"no-border\" | \"primary\" | ButtonVariant.Default | ButtonVariant.NoBorder | ButtonVariant.Primary",
                    "references": {
                        "ButtonVariant": {
                            "location": "import",
                            "path": "./button.enum",
                            "id": "src/components/button/button.enum.ts::ButtonVariant"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Style variant of the button. Note that not all variants are supported by every theme."
                },
                "attribute": "variant",
                "reflect": false,
                "defaultValue": "ButtonVariant.Default"
            },
            "href": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Define href for button. If set button will be semantical rendered as link (<a>)"
                },
                "attribute": "href",
                "reflect": true
            },
            "download": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true`, the component adds a download attribute to the a tag"
                },
                "attribute": "download",
                "reflect": false,
                "defaultValue": "false"
            },
            "target": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "_self"
                        }],
                    "text": "Define link target, _self, _blank"
                },
                "attribute": "target",
                "reflect": false,
                "defaultValue": "\"_self\""
            }
        };
    }
    static get elementRef() { return "host"; }
    static get listeners() {
        return [{
                "name": "click",
                "method": "onClick",
                "target": undefined,
                "capture": true,
                "passive": false
            }, {
                "name": "disabled",
                "method": "checkDisabled",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
//# sourceMappingURL=button.js.map
